import type { ReactNode } from "react";

import Nav from "../../components/marketing/Nav";
import Footer from "../../components/marketing/Footer";
import { config } from "../../config";

import "./LegalLayout.css";

interface LegalLayoutProps {
  title: string;
  /** Date the document was last revised, displayed under the title. */
  lastUpdated: string;
  children: ReactNode;
}

/**
 * Shared chrome for /privacy and /terms.
 *
 * Renders the same Nav and Footer as the marketing pages so the brand
 * lockup and the JoatLabs.dev parent line stay consistent. The body slot
 * is just JSX -- no markdown renderer at runtime, so headings, lists, and
 * links can be styled directly with the design tokens.
 */
export default function LegalLayout({
  title,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  // Both routes are static, so carry explicit attribution back to the landing
  // page and let LandingPage open the waitlist dialog on arrival.
  const goToWaitlist = () => {
    window.location.href = "/?waitlist=legal-page";
  };

  return (
    <>
      <Nav onWaitlistClick={goToWaitlist} />
      <main className="oc-legal">
        <div className="oc-legal-inner">
          <header className="oc-legal-header">
            <h1>{title}</h1>
            <p className="oc-legal-meta">
              Last updated: {lastUpdated}
              <span className="oc-legal-sep" aria-hidden="true">
                {" "}&middot;{" "}
              </span>
              <span>
                {config.brandName} is operated by{" "}
                {config.parentUrl ? (
                  <a href={config.parentUrl} target="_blank" rel="noreferrer">
                    {config.parentName}
                  </a>
                ) : (
                  config.parentName
                )}
              </span>
            </p>
            <p className="oc-legal-disclaimer">
              This document is a plain-English starting point. It has not been
              reviewed by counsel. Please consult a qualified lawyer before
              relying on it as a final published policy.
            </p>
          </header>
          <article className="oc-legal-body">{children}</article>
        </div>
      </main>
      <Footer />
    </>
  );
}
